/**
 * KioskGrid — Grid of staff members with status indicators
 */

import clsx from 'clsx';
import type { KioskStaffState, KioskStatus } from '../../stores/workforce-store';

interface KioskGridProps {
  staffStates: KioskStaffState[];
  onSelect: (staffId: string) => void;
}

function formatTime(isoString: string | null): string {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleTimeString('nl-NL', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatusIndicator({ status }: { status: KioskStatus }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={clsx(
          'w-2.5 h-2.5 rounded-full',
          status === 'in' && 'bg-emerald-500',
          status === 'out' && 'bg-slate-300',
          status === 'break' && 'bg-amber-500'
        )}
      />
      <span
        className={clsx(
          'text-sm font-medium',
          status === 'in' && 'text-emerald-600',
          status === 'out' && 'text-slate-400',
          status === 'break' && 'text-amber-600'
        )}
      >
        {status === 'in' && 'Aan het werk'}
        {status === 'out' && 'Niet ingeklokt'}
        {status === 'break' && 'Pauze'}
      </span>
    </div>
  );
}

export function KioskGrid({ staffStates, onSelect }: KioskGridProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
      {staffStates.map(({ staff, status, clockInTime }) => (
        <button
          key={staff.id}
          onClick={() => onSelect(staff.id)}
          className={clsx(
            'flex flex-col items-center gap-3 p-6 rounded-2xl border-2 transition-all',
            'hover:shadow-lg active:scale-[0.98]',
            status === 'in' && 'bg-emerald-50 border-emerald-200',
            status === 'out' && 'bg-white border-slate-200 hover:border-slate-300',
            status === 'break' && 'bg-amber-50 border-amber-200'
          )}
        >
          {/* Avatar */}
          <div
            className={clsx(
              'w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold',
              status === 'in' && 'bg-emerald-500 text-white',
              status === 'out' && 'bg-slate-200 text-slate-500',
              status === 'break' && 'bg-amber-500 text-white'
            )}
          >
            {staff.name.charAt(0).toUpperCase()}
          </div>

          {/* Name */}
          <div className="text-center">
            <p className="font-semibold text-slate-800 text-lg">{staff.name.split(' ')[0]}</p>
            <StatusIndicator status={status} />
          </div>

          {/* Clock-in time */}
          {status !== 'out' && (
            <p className="text-xs text-slate-400">
              Sinds {formatTime(clockInTime)}
            </p>
          )}
        </button>
      ))}
    </div>
  );
}
